import { useEffect } from "react";
import { getPublicConfig, chatAuth } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Loads the Tawk.to widget once. If a user is logged in, calls the backend
 * /chat/auth endpoint to get HMAC + attributes and pushes them into the widget
 * (Secure Mode + visitor identity + 6-digit support_pin custom attribute).
 *
 * Renders nothing — Tawk injects its own floating widget.
 */
export default function TawkChat() {
  const { user, token } = useAuth();

  useEffect(() => {
    let cancelled = false;
    getPublicConfig().then((cfg) => {
      if (cancelled || !cfg?.tawk_enabled) return;
      if (window.Tawk_API) return; // already loaded

      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // If we have a logged-in user, request HMAC + attributes BEFORE Tawk loads
      const apply = async () => {
        if (token && user) {
          try {
            const auth = await chatAuth();
            window.Tawk_API.onLoad = function () {
              try {
                window.Tawk_API.setAttributes({
                  name: auth.name,
                  email: auth.email,
                  hash: auth.hash,
                  ...auth.attributes,
                }, () => {});
              } catch (e) { /* ignore */ }
            };
            // ensure setAttributes runs if widget is already loaded by the time we attach
            window.Tawk_API.visitor = { name: auth.name, email: auth.email, hash: auth.hash };
          } catch (e) {
            // anonymous visitor — that's fine
          }
        }

        const s = document.createElement("script");
        s.async = true;
        s.src = `https://embed.tawk.to/${cfg.tawk_property_id}/${cfg.tawk_widget_id}`;
        s.charset = "UTF-8";
        s.setAttribute("crossorigin", "*");
        document.body.appendChild(s);
      };
      apply();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]);

  return null;
}
